/**
 * Local-first attempt store (PLAN-Z M5, PLAN.md §5): IndexedDB via
 * idb-keyval. Every attempt is written the instant it is answered, keyed by a
 * client-generated UUID so persistence and (later, M9) sync are idempotent —
 * a retried write can never double-count. The network is never on the
 * critical path of answering a question.
 */
import { clear, createStore, entries, set } from 'idb-keyval'
import type { Difficulty, Subtest } from '../generators/types.ts'

export interface Attempt {
  /** Client-generated UUID — the primary key locally and in Postgres (M9). */
  id: string
  /** Groups the attempts of one drill/exam session. */
  sessionId: string
  /**
   * Identifies the solved ITEM. Figure items (M7) produce two responses that
   * share one itemId and one duration — accuracy counts responses, timing
   * counts items (PLAN-Z M5).
   */
  itemId: string
  subtest: Subtest
  difficulty: Difficulty
  seed: number
  correct: boolean
  /** Per-letter (equations) / per-blank (figures) / per-item (latin) detail. */
  subAnswers: Record<string, unknown>
  durationMs: number
  answeredAt: string // ISO timestamp
}

const store = createStore('dmat-prep', 'attempts')

/** Client-generated id with a fallback for environments without crypto.randomUUID. */
export function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export async function saveAttempt(attempt: Attempt): Promise<void> {
  await set(attempt.id, attempt, store)
}

/** All stored attempts, oldest first. */
export async function loadAttempts(): Promise<Attempt[]> {
  const list = await entries<string, Attempt>(store)
  return list.map(([, attempt]) => attempt).sort((a, b) => a.answeredAt.localeCompare(b.answeredAt))
}

/** Dev/test helper: wipes local history. */
export async function clearAttempts(): Promise<void> {
  await clear(store)
}
